/**
 * Cálculo do desconto de cupom = FONTE ÚNICA NO BACKEND.
 *
 * O preço com cupom é calculado no servidor (cupom-service.obterDescontoParaPlano)
 * e vem pronto em `cupom.descontos_calculados` (mapa { id_plano: detalhe }) nos
 * endpoints /meu-cupom e /validar-cupom-publico. O front NÃO recalcula — só lê
 * esse resultado. Assim o preview nunca diverge do que é cobrado e não há como
 * "burlar" o valor pela tela (o back recalcula de novo na geração da cobrança).
 */

const fallbackDetalhe = (plano) => {
	const valorFixo = parseFloat(plano?.valor_fixo || 0);
	return {
		aplicavel: false,
		tipo: "PERCENTUAL",
		percentual: 0,
		valor_base: valorFixo,
		valor_desconto: 0,
		valor_final: valorFixo,
	};
};

/**
 * Detalhamento do desconto pra um plano, calculado no backend.
 */
export const obterDetalhesDescontoCupom = (plano, cupom) => {
	const detalhe = cupom?.descontos_calculados?.[plano?.id];
	if (detalhe && typeof detalhe === "object") return detalhe;
	return fallbackDetalhe(plano);
};

/** O cupom incide neste plano? (decidido no backend) */
export const cupomSeAplicaAoPlano = (plano, cupom) =>
	obterDetalhesDescontoCupom(plano, cupom).aplicavel === true;

/** valor_fixo do plano após o cupom (calculado no backend). */
export const aplicarCupomNoValorFixo = (plano, cupom) => {
	const d = obterDetalhesDescontoCupom(plano, cupom);
	return d.aplicavel ? d.valor_final : parseFloat(plano?.valor_fixo || 0);
};

/**
 * Números de EXIBIÇÃO do cupom no checkout (CheckoutPagamento), derivados do
 * detalhe calculado no back:
 *
 * - `descontoFixo`: delta que sai do valor em cobrança = fixo de CATÁLOGO do
 *   plano − valor_final (a MESMA conta do descontoCupomDoPlano do back). Com
 *   cupom de plano-base (id_plano_base_calculo), o `valor_desconto` do detalhe
 *   vem calculado na base do OUTRO plano (ex.: 40% do mensal×12 = 527,52) e
 *   NÃO é o delta cobrado — usá-lo direto foi o bug do ELEVAR_NIVEL_2026
 *   (tela mostrava 432,38 e a cobrança real saía 791,28).
 * - `ancoraFixo`: valor riscado. Com plano-base, a referência visual é a BASE
 *   do cupom (mensal×12 riscado → valor_final), o enquadramento da campanha;
 *   sem plano-base degenera pro fixo em cobrança (comportamento antigo).
 * - `economiaCupom`: "você economizou" coerente com a âncora exibida
 *   (âncora − valor final do fixo), sem misturar créditos da conta.
 *
 * `valorEmCobranca` é o fixo sendo cobrado nesta tela (fixo cheio na compra;
 * na troca-ciclo já vem com o resíduo abatido — por isso o delta deriva do
 * fixo de catálogo, não dele). Clamps: cupom nunca vira sobretaxa nem passa
 * do valor em cobrança. O back segue autoritativo.
 */
export const resumoExibicaoCupom = (plano, cupom, valorEmCobranca) => {
	const base = Math.max(0, Number(valorEmCobranca) || 0);
	const detalhe = obterDetalhesDescontoCupom(plano, cupom);
	if (!detalhe.aplicavel) {
		return {
			aplicavel: false,
			descontoFixo: 0,
			ancoraFixo: base,
			economiaCupom: 0,
		};
	}
	const planoFixo = parseFloat(plano?.valor_fixo || 0);
	const valorFinal = Number(detalhe.valor_final);
	const descontoFixo = Number.isFinite(valorFinal)
		? Math.min(base, Math.max(0, planoFixo - valorFinal))
		: 0;
	const ancoraFixo = Math.max(base, Number(detalhe.valor_base) || 0);
	const economiaCupom = Math.max(0, ancoraFixo - base) + descontoFixo;
	return { aplicavel: true, descontoFixo, ancoraFixo, economiaCupom };
};

/**
 * Formata a porcentagem com vírgula decimal (padrão pt-BR).
 */
export const formatarPorcentagemCupom = (valor) => {
	const num = Number(valor);
	if (!Number.isFinite(num)) return "";
	return Number.isInteger(num)
		? `${num}%`
		: `${num.toString().replace(".", ",")}%`;
};
